import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	ViewChild,
	computed,
	effect,
	input,
	output
} from '@angular/core';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { Subject, Subscription, debounceTime, map } from 'rxjs';
import { FormFieldComponent } from '../form-field.component.js';

@Component({
	selector: 'ui-text-form-field',
	templateUrl: './text-form-field.component.html',
	styleUrls: ['../form-fields.shared.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TextFormFieldComponent extends FormFieldComponent<string> {
	private _textValueChangeSubscription: Subscription | undefined;
	private readonly _textValueChange = new Subject<string | undefined>();

	public readonly maxLength = input<number>();
	public readonly validation = input<'email'>();
	public readonly useDebounce = input<boolean>();
	public readonly disableAutocomplete = input<boolean>(false);
	public readonly textType = input<'text' | 'password'>('text');

	/**
	 * Output fired immediately even if debounce is used
	 */
	public readonly fastInputChange = output<string | undefined>();

	protected readonly autocompleteValue = computed<string>(() => {
		if (this.disableAutocomplete()) {
			return 'new-password';
		}
		return 'off';
	});

	private readonly debounceTime = computed<number>(() => {
		if (this.isMobileView()) {
			return 1400;
		}

		return 900;
	});

	@ViewChild('input') inputElement?: ElementRef;

	constructor() {
		super();

		effect(() => {
			this._textValueChangeSubscription?.unsubscribe?.();
			const useDebounce = this.useDebounce();

			if (useDebounce) {
				this.subscribeToValueChange();
			}
		});
	}

	handleModelChange(value?: string): void {
		this.fastInputChange.emit(value);

		if (this._textValueChangeSubscription) {
			// update value via debounce subscription
			this._textValueChange.next(value);
		} else {
			// update value immediately
			super.updateValue(value);
		}
	}

	protected getHint(value?: string): string | undefined {
		const maxLength = this.maxLength();
		if (maxLength && value && value?.length === maxLength) {
			return `Max length reached (${maxLength} chars)`;
		}

		if (value && this.validation() === 'email' && !stringHelper.isValidEmail(value ?? '')) {
			return `Invalid e-mail`;
		}

		return undefined;
	}

	protected isValueValid(value?: string): boolean {
		const maxLength = this.maxLength();
		if (maxLength) {
			if (!value) {
				return false;
			}
			if (value.length > maxLength) {
				return false;
			}
		}

		if (this.validation() === 'email') {
			return stringHelper.isValidEmail(value ?? '');
		}

		return true;
	}

	private subscribeToValueChange(): void {
		this._textValueChangeSubscription = super.subscribeToObservable(
			this._textValueChange.pipe(
				debounceTime(this.debounceTime()),
				map((value) => {
					super.updateValue(value);
				})
			)
		);
	}
}
